/**
 * WelcomeBanner — top-of-page personalised banner shown to visitors
 * arriving from /r/:code or /aff/:code (i.e. localStorage has trp_aff_code).
 *
 * Self-contained: only renders if (a) localStorage has a code, (b) the
 * partner has welcome_banner_enabled=true on the backend, and (c) the
 * visitor hasn't dismissed it in this session. Skipped on routes where
 * the banner would compete with the primary action (admin, affiliate,
 * lifetime, preview, tools, affiliate-kit, /vs/* lives on its own).
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { X, Gift, Sparkles } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { API } from "../config";
import { getStoredAffCode } from "../pages/AffiliateRedirect";

const SESSION_DISMISS_KEY = "trp_welcome_dismissed";
const HIDDEN_PREFIXES = [
  "/dashboard", "/admin", "/affiliate", "/lifetime",
  "/preview", "/auth", "/legal",
];

const COPY = {
  sk: {
    greeting: (n) => `${n} ťa odporučil`,
    discount: (p, c) => `Získaj ${p}% zľavu kódom`,
    dismiss: "Zavrieť",
  },
  en: {
    greeting: (n) => `${n} referred you`,
    discount: (p, c) => `Get ${p}% off with code`,
    dismiss: "Dismiss",
  },
};

export function WelcomeBanner() {
  const { language } = useLanguage();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const t = COPY[language] || COPY.en;

  // Respect this-session dismissal (sessionStorage, not localStorage —
  // intentional: we want to re-greet on the next visit so it stays salient).
  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") setDismissed(true);
    } catch { /* ignore */ }
  }, []);

  // Fetch the welcome payload once on mount when we have a stored code.
  useEffect(() => {
    const code = getStoredAffCode();
    if (!code || dismissed) return;
    let cancelled = false;
    axios.get(`${API}/affiliate/welcome/${encodeURIComponent(code)}`)
      .then(r => { if (!cancelled && r.data?.enabled) setData(r.data); })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [dismissed]);

  const isHidden = HIDDEN_PREFIXES.some(p => location.pathname.startsWith(p));
  if (isHidden || dismissed || !data) return null;

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  const greetingLine = data.custom_greeting || t.greeting(data.name);

  return (
    <div
      className="w-full bg-gradient-to-r from-[#FFE600] to-yellow-300 text-black border-b-2 border-yellow-500"
      data-testid="welcome-banner"
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm font-medium min-w-0">
          <Sparkles className="w-4 h-4 flex-shrink-0" />
          <span className="font-bold truncate">{greetingLine}.</span>
          <span className="hidden sm:inline-flex items-center gap-1.5">
            <Gift className="w-4 h-4" />
            <span>{t.discount(data.discount_percent)} </span>
            <code className="px-2 py-0.5 bg-black text-[#FFE600] font-black rounded text-xs tracking-wider">
              {data.discount_code}
            </code>
          </span>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t.dismiss}
          className="flex-shrink-0 w-6 h-6 flex items-center justify-center text-black/60 hover:text-black hover:bg-black/10 rounded"
          data-testid="welcome-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {/* Mobile-only discount line — keeps the desktop bar single-row clean. */}
      <div className="sm:hidden px-4 pb-2 text-[11px] text-black/80 -mt-1 flex items-center gap-1.5">
        <Gift className="w-3 h-3" />
        {t.discount(data.discount_percent)}
        <code className="px-1.5 py-0.5 bg-black text-[#FFE600] font-black rounded tracking-wider">
          {data.discount_code}
        </code>
      </div>
    </div>
  );
}

export default WelcomeBanner;
